import { auditLogger, isValidUUID, PersistentRateLimiter, sanitizeErrorMessage } from 'src/utils/security';
import { getCurrentUser, getSupabase } from 'src/utils/supabase';

interface SecureServiceConfig {
  requireAuth?: boolean;
  rateLimitKey?: string;
  rateLimitMax?: number;
  rateLimitWindowMs?: number;
}

const rateLimiters: Map<string, PersistentRateLimiter> = new Map();

function getRateLimiter(key: string, max: number, windowMs: number): PersistentRateLimiter {
  const limiterKey = `${key}_${max}_${windowMs}`;
  if (!rateLimiters.has(limiterKey)) {
    rateLimiters.set(limiterKey, new PersistentRateLimiter(max, windowMs, `svc_${key}`));
  }
  return rateLimiters.get(limiterKey) as PersistentRateLimiter;
}

export async function withSecurity<T>(operation: () => Promise<T>, config: SecureServiceConfig = {}): Promise<T> {
  const { requireAuth = true, rateLimitKey, rateLimitMax = 100, rateLimitWindowMs = 60000 } = config;

  if (requireAuth) {
    const user = await getCurrentUser();
    if (!user) {
      if (!localStorage.getItem('isAuthenticated')) {
        return new Promise<T>(() => {});
      }
      auditLogger.log('AUTH_FAILURE', { reason: 'no_user', service: rateLimitKey || 'unknown' });
      throw new Error('Authentication required');
    }

    if (rateLimitKey) {
      const limiter = getRateLimiter(rateLimitKey, rateLimitMax, rateLimitWindowMs);
      if (!limiter.canAttempt(user.id)) {
        auditLogger.log('RATE_LIMIT_HIT', { key: rateLimitKey, userId: user.id.substring(0, 8) });
        throw new Error('Rate limit exceeded. Please try again later.');
      }
    }
  }

  try {
    return await operation();
  } catch (error) {
    throw new Error(sanitizeErrorMessage(error));
  }
}

export async function verifyOwnership(table: string, resourceId: string, ownerField = 'profile_id'): Promise<boolean> {
  if (!isValidUUID(resourceId)) {
    auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_uuid', table, resourceId: resourceId.substring(0, 8) });
    return false;
  }

  const user = await getCurrentUser();
  if (!user) {
    auditLogger.log('AUTH_FAILURE', { reason: 'no_user_ownership_check', table });
    return false;
  }

  const supabase = await getSupabase();
  const { data, error } = await supabase.from(table).select(ownerField).eq('id', resourceId).maybeSingle();

  if (error || !data) {
    return false;
  }

  const ownerValue = data[ownerField as keyof typeof data];
  const isOwner = ownerValue === user.id;

  if (!isOwner) {
    auditLogger.log('OWNERSHIP_VIOLATION', {
      table,
      resourceId: resourceId.substring(0, 8) + '...',
      userId: user.id.substring(0, 8) + '...',
    });
  }

  return isOwner;
}

export async function verifyOwnershipByField(
  table: string,
  fieldName: string,
  fieldValue: string,
  ownerField = 'profile_id'
): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) {
    return false;
  }

  const supabase = await getSupabase();
  const { data, error } = await supabase.from(table).select(ownerField).eq(fieldName, fieldValue).maybeSingle();

  if (error || !data) {
    return false;
  }

  const ownerValue = data[ownerField as keyof typeof data];
  return ownerValue === user.id;
}

export async function getCurrentUserId(): Promise<string | null> {
  const user = await getCurrentUser();
  return user?.id || null;
}

export async function requireAuth(): Promise<string> {
  const user = await getCurrentUser();
  if (!user) {
    if (!localStorage.getItem('isAuthenticated')) {
      return new Promise<string>(() => {});
    }
    auditLogger.log('AUTH_FAILURE', { reason: 'require_auth_failed' });
    throw new Error('Authentication required');
  }
  return user.id;
}
